// ═══════════════════════════════════════════════
// ECharts 翻译: 图表文字画在 canvas 上, DOM 翻译层看不到, 只能在 setOption 入口处理。
//
// 做法: 给 ECharts 实例原型的 setOption 套一层, 英文模式下把 option 里的字符串
// (标题、坐标轴名、图例、系列名、类目) 按字典翻译; formatter 函数包一层, 翻译其返回值。
// 每个实例记下原始 option 调用序列, 切换语言时按序重放, 图表立即换语言。
// ═══════════════════════════════════════════════
import { translateHtmlString, type TextTranslator } from './textTranslator';

type ChartInstance = {
  setOption: (option: unknown, ...rest: unknown[]) => unknown;
  getOption: () => unknown;
  isDisposed: () => boolean;
};
type SetOption = ChartInstance['setOption'];
type History = Array<{ option: unknown; rest: unknown[] }>;

let translator: TextTranslator | null = null;
let originalSetOption: SetOption | null = null;
const histories = new WeakMap<object, History>();
const instances = new Set<ChartInstance>();   // 已销毁的实例在 prune 时移除
const MAX_HISTORY = 30;

function isNotMerge(rest: unknown[]) {
  const first = rest[0];
  return first === true || (typeof first === 'object' && first !== null && (first as { notMerge?: boolean }).notMerge === true);
}

function deepTranslate(value: unknown, t: TextTranslator, key = ''): unknown {
  if (typeof value === 'string') return translateHtmlString(t, value);
  if (typeof value === 'function') {
    const fn = value as (...args: unknown[]) => unknown;
    return function wrapped(this: unknown, ...args: unknown[]) {
      const out = fn.apply(this, args);
      return typeof out === 'string' ? translateHtmlString(t, out) : out;
    };
  }
  if (Array.isArray(value)) {
    // 大数组 (曲线数据) 第一个元素是数字就整个跳过
    if (value.length > 50 && (typeof value[0] === 'number' || Array.isArray(value[0]))) return value;
    return value.map((item) => deepTranslate(item, t));
  }
  if (value && typeof value === 'object') {
    if (Object.getPrototypeOf(value) !== Object.prototype) return value; // 渐变色对象、DOM 等
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // legend.selected 这类以系列名为键的映射, 键也要翻
      const outKey = key === 'selected' ? translateHtmlString(t, k) : k;
      out[outKey] = deepTranslate(v, t, k);
    }
    return out;
  }
  return value;
}

function track(inst: ChartInstance, option: unknown, rest: unknown[]) {
  let history = histories.get(inst);
  if (!history) {
    history = [];
    histories.set(inst, history);
    instances.add(inst);
    if (instances.size > 200) prune();
  }
  if (isNotMerge(rest)) history.length = 0;
  history.push({ option, rest });
  if (history.length > MAX_HISTORY) history.splice(0, history.length - MAX_HISTORY);
}

function prune() {
  for (const inst of instances) if (inst.isDisposed()) instances.delete(inst);
}

function replay(inst: ChartInstance) {
  const history = histories.get(inst);
  if (!history?.length || !originalSetOption) return;
  history.forEach(({ option, rest }, i) => {
    const next = translator ? deepTranslate(option, translator) : option;
    const restArgs = i === 0 ? [true] : rest;  // 第一次全量替换, 清掉另一种语言的残留
    originalSetOption!.call(inst, next, ...restArgs);
  });
}

/** 打补丁 (只需一次) 并切换图表语言; translatorOrNull 为 null 表示回到中文。 */
export async function setChartLanguage(translatorOrNull: TextTranslator | null) {
  translator = translatorOrNull;
  if (!originalSetOption) {
    if (!translator) return;   // 一直是中文就不必加载 echarts, 也不必打补丁
    const echarts = await import('echarts');
    const probe = echarts.init(document.createElement('div')) as unknown as ChartInstance;
    const proto = Object.getPrototypeOf(probe) as { setOption: SetOption };
    (probe as unknown as { dispose: () => void }).dispose();
    originalSetOption = proto.setOption;
    proto.setOption = function patched(this: ChartInstance, option: unknown, ...rest: unknown[]) {
      track(this, option, rest);
      const next = translator ? deepTranslate(option, translator) : option;
      return originalSetOption!.call(this, next, ...rest);
    };
    // 补丁之前已经画好的图: 以当前 option 作为原始记录
    document.querySelectorAll('[_echarts_instance_]').forEach((el) => {
      const inst = echarts.getInstanceByDom(el as HTMLElement) as unknown as ChartInstance | undefined;
      if (inst && !histories.has(inst)) track(inst, inst.getOption(), [true]);
    });
  }
  prune();
  instances.forEach(replay);
}
